import React, { useState } from "react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { UserRole } from "model/Company";
import type { CompanyUser } from "model/Company";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyUserForm } from "web-apps/company/components/companySettingsV2/companyUsers/companyUserForm.component";
import {
  ActionButtons,
  IconButton,
  ModalActions,
} from "web-apps/company/components/companySettingsV2/companyUsers/companyUsers.styled";
import {
  StampClockAccessHeader,
  StampClockCreateActions,
  StampClockCreateDescription,
  StampClockCreatedEmail,
  StampClockEmptyText,
  StampClockUserEmail,
  StampClockMetaLabel,
  StampClockUserList,
  StampClockUserName,
  StampClockUserRow,
} from "./companyStampClock.styled";
import {
  CopyStampClockLinkButton,
  getStampClockUrl,
} from "./stampClockLink.component";

type DialogState =
  | { readonly type: "closed" }
  | { readonly type: "create" }
  | { readonly email: string; readonly type: "created" }
  | { readonly type: "edit"; readonly user: CompanyUser }
  | { readonly type: "delete"; readonly user: CompanyUser };

function CreatedContent(props: { readonly email: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <StampClockCreateDescription>
        {t("CompanyStampClock.CreatedDescription")}
      </StampClockCreateDescription>
      <StampClockCreatedEmail>
        <strong>{t("CompanyStampClock.EmailLabel")}:</strong> {props.email}
      </StampClockCreatedEmail>
    </>
  );
}

export const CreateStampClock: React.FC = () => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [companyState, companyActions] = useCompanyStore();
  const [dialog, setDialog] = useState<DialogState>({ type: "closed" });
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCreated = dialog.type === "created";
  const stampClockUrl = getStampClockUrl();
  const stampClockUsers = companyState.users.filter(
    ({ role }) => role === UserRole.COMPANY_TIMESTAMP,
  );

  const closeDialog = (): void => {
    if (isSubmitting || isRemoving) return;
    setDialog({ type: "closed" });
  };

  const removeUser = async (): Promise<void> => {
    if (!companyState.company || dialog.type !== "delete" || isRemoving) return;
    setIsRemoving(true);
    try {
      await companyActions.removeCompanyUser(
        companyState.company.id,
        dialog.user.id,
      );
      setDialog({ type: "closed" });
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("CompanyUsers.CouldNotRemoveUser"),
        },
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <>
      <StampClockAccessHeader>
        <div>
          <StampClockMetaLabel>
            {t("CompanyStampClock.UsersLabel")}
          </StampClockMetaLabel>
          <StampClockUserList>
            {stampClockUsers.length > 0 ? (
              stampClockUsers.map((user) => {
                const name = [user.firstName, user.lastName]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <StampClockUserRow key={user.id}>
                    <StampClockUserName>{name || "–"}</StampClockUserName>
                    <StampClockUserEmail>{user.email}</StampClockUserEmail>
                    {companyState.companyUser?.role ===
                      UserRole.COMPANY_OWNER &&
                      user.email !== companyState.companyUser.email && (
                        <ActionButtons>
                          <TooltipHover text={t("General.Edit")}>
                            <IconButton
                              type="button"
                              aria-label={t("General.Edit")}
                              onClick={() => setDialog({ type: "edit", user })}
                            >
                              <CustomIcon
                                name="v2/pen"
                                size="20px"
                                color={ColorV2.Blue1}
                              />
                            </IconButton>
                          </TooltipHover>
                          <TooltipHover text={t("General.Delete")}>
                            <IconButton
                              type="button"
                              aria-label={t("General.Delete")}
                              onClick={() =>
                                setDialog({ type: "delete", user })
                              }
                            >
                              <CustomIcon
                                name="v2/trash"
                                size="20px"
                                color={ColorV2.NegativeRed}
                              />
                            </IconButton>
                          </TooltipHover>
                        </ActionButtons>
                      )}
                  </StampClockUserRow>
                );
              })
            ) : (
              <StampClockUserRow>
                <StampClockEmptyText>
                  {t("CompanyStampClock.NoUsers")}
                </StampClockEmptyText>
              </StampClockUserRow>
            )}
          </StampClockUserList>
        </div>
        <StampClockCreateActions>
          <PrimaryButton
            label={t("CompanyStampClock.CreateButton")}
            iconName="v2/plus"
            onClick={() => setDialog({ type: "create" })}
          />
        </StampClockCreateActions>
      </StampClockAccessHeader>
      <Modal
        label={t(
          isCreated
            ? "CompanyStampClock.CreatedTitle"
            : "CompanyStampClock.CreateTitle",
        )}
        isOpen={dialog.type === "create" || dialog.type === "created"}
        onClose={closeDialog}
        size="compact"
        showCloseButton={false}
        footer={
          isCreated ? (
            <>
              <SecondaryButton
                label={t("General.Cancel")}
                onClick={closeDialog}
              />
              <CopyStampClockLinkButton url={stampClockUrl} variant="primary" />
            </>
          ) : undefined
        }
      >
        {dialog.type === "created" ? (
          <CreatedContent email={dialog.email} />
        ) : (
          <>
            <StampClockCreateDescription>
              {t("CompanyStampClock.CreateDescription")}
            </StampClockCreateDescription>
            <CompanyUserForm
              mode="stampClock"
              onCancel={closeDialog}
              onCreated={(email) => setDialog({ email, type: "created" })}
              onSubmittingChange={setIsSubmitting}
            />
          </>
        )}
      </Modal>
      <Modal
        label={t("CompanyUsers.UpdateUser")}
        isOpen={dialog.type === "edit"}
        onClose={closeDialog}
        size="small"
        showCloseButton={false}
      >
        {dialog.type === "edit" && (
          <CompanyUserForm
            currentUser={dialog.user}
            mode="stampClock"
            onCancel={closeDialog}
            onUpdated={closeDialog}
            onSubmittingChange={setIsSubmitting}
          />
        )}
      </Modal>
      <Modal
        label={t("CompanyUsers.RemoveCompanyUser")}
        isOpen={dialog.type === "delete"}
        onClose={closeDialog}
        size="compact"
        showCloseButton={false}
        footer={
          <ModalActions>
            <SecondaryButton
              label={t("General.Cancel")}
              disabled={isRemoving}
              onClick={closeDialog}
            />
            <PrimaryButton
              label={t("General.Remove")}
              isLoading={isRemoving}
              styleOverrides={{
                backgroundColor: ColorV2.NegativeRed,
                hoverBackgroundColor: ColorV2.DeclinedRed,
                activeBackgroundColor: ColorV2.DeclinedRed,
                textColor: ColorV2.White,
                borderColor: "transparent",
                iconColor: ColorV2.White,
              }}
              onClick={() => void removeUser()}
            />
          </ModalActions>
        }
      >
        {dialog.type === "delete" && (
          <StampClockCreateDescription>
            {dialog.user.email}
          </StampClockCreateDescription>
        )}
      </Modal>
    </>
  );
};
